import apiV2 from "api-v2";
import type { AxiosResponse } from "axios";
import { toFormData } from "axios";
import type { ApiResponse } from "types/common.types";
import { showToast } from "utils/helpers";

import type { UserCompanyProfile } from "newui/features/Auth";
import type { User } from "newui/features/Auth/types/User.type";

import type { RfxSortValue } from "../config/rfx-sort-options";

import type { GuestCompanyData } from "./types/guest-company-data.type";
import type { RfxQuoteAnswers } from "./types/quote.type";
import type { RfxAnswers } from "./types/returnable-schedule-answer.type";
import type { RfxSubmission } from "./types/submission.type";

interface AcceptDeclineSubmissionResponse {
	company: UserCompanyProfile;
	user: User;
}

export const acceptDeclineSubmission = async (
	rfxId: number,
	hasAccepted: boolean,
	reason: string,
	companyData: GuestCompanyData | null = null
): Promise<AcceptDeclineSubmissionResponse | null> => {
	const data = toFormData({
		hasAccepted: hasAccepted ? 1 : 0,
		reason: reason,
		companyData: companyData,
	});
	try {
		const response = await apiV2.post<
			AxiosResponse<AcceptDeclineSubmissionResponse>
		>(`/rfq/${rfxId}/accept-or-decline`, data);
		if (response.status === 200) {
			return response.data.data;
		} else {
			return null;
		}
	} catch (e) {
		return null;
	}
};

export const getRfxSubmissionByRfxAndCompanyId = async (
	rfxId: number,
	submissionCompanyId: number
): Promise<number | null> => {
	try {
		const response = await apiV2.get(
			`rfx-invite/${rfxId}/get-submission-by-company/${submissionCompanyId}`
		);

		if (response.status === 200) {
			const rfxInviteId = response?.data?.data as number;
			return rfxInviteId;
		} else {
			return null;
		}
	} catch (err) {
		showToast(err?.message, "Error");
		return null;
	}
};

/**
 *
 * @param rfxInviteId
 * @returns
 */
export const getCompanySubmission = async (
	rfxInviteId: number
): Promise<RfxSubmission> => {
	try {
		const response = await apiV2.get(`rfx-invite/${rfxInviteId}`);

		if (response.status === 200) {
			const submissionData = response?.data?.data as RfxSubmission;
			return submissionData;
		} else {
			return null;
		}
	} catch (err) {
		showToast(err?.message, "Error");
		return null;
	}
};

/**
 * @param rfxId
 * @param guestId
 * @returns
 */
export const getGuestRfxInvitation = async (
	rfxId: number,
	guestId: number
): Promise<RfxSubmission> => {
	try {
		// guest check this is only needed when fetching the submission prior to accepting it
		const response = await apiV2.post(`guest/show-submission/${rfxId}`, {
			guest_user_id: guestId,
		});
		if (response.status === 200) {
			const submissionData = response?.data?.data as RfxSubmission;
			return submissionData;
		} else {
			return null;
		}
	} catch (err) {
		showToast(err?.message, "Error");
		return null;
	}
};

export const revokeSubmission = async (
	submissionId: number
): Promise<boolean> => {
	const result = await apiV2.patch<ApiResponse<boolean>>(
		`rfx-invite/${submissionId}/revoke-submission`
	);

	return result.data?.data;
};

export const saveSubmission = async (
	submissionId: number,
	value: { question_answer: RfxAnswers; quote_answer: RfxQuoteAnswers }
) => {
	const formData = toFormData(value);
	try {
		const response = await apiV2.post(
			`rfx-invite/${submissionId}/save-submission`,
			formData
		);
		if (response.status === 200) {
			return response.data;
		} else {
			showToast("Failed to save submission", "Error");
			return false;
		}
	} catch (e) {
		console.log(e);
		showToast("Failed to save submission", "Error");
	}
};

export const submitSubmission = async (
	rfxInviteId: number,
	hasAgreedTerms: boolean
): Promise<string | null> => {
	const formData = toFormData({ hasAgreedTerms: hasAgreedTerms ? 1 : 0 });
	const apiRoute = `rfx-invite/${rfxInviteId}/submit-submission`;
	try {
		const response = await apiV2.post(apiRoute, formData);
		if (response.status === 200) {
			showToast(response.data.message, "Success", true);
			return response.data?.data?.submittedAt;
		} else {
			showToast(response.data.message, "Error");
			return null;
		}
	} catch (e) {
		return null;
	}
};

export const getAllSubmissions = async (
	page: number,
	search: string,
	limit: number,
	// TASK-036 (typecheck sweep): callers pass an AbortSignal (axios config) —
	// previously silently dropped; now forwarded so in-flight fetches abort.
	sort?: RfxSortValue,
	config?: { signal?: AbortSignal }
) => {
	// Built with URLSearchParams so `search` is encoded — it was previously
	// interpolated raw, so a query containing `&` or `#` corrupted the URL.
	const params = new URLSearchParams({ page: String(page), limit: String(limit) });

	if (search.trim() !== "") {
		params.set("search", search);
	}

	if (sort) {
		params.set("sort", sort);
	}

	return await apiV2(`/rfq/supplier-index?${params.toString()}`, config);
};
